import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchMap,
  selectiDB,
  selectMap,
  selectVersion,
} from "../../features/counter/counterSlice";
import generateMenuFromXml from "../../components/GuidelineNav/menuGeneration";

export const useDynamicContentLoader = ({
  contentPattern,
  chapterRoot,
  link,
}) => {
  const [htmlContent, setHtmlContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const iDB = useSelector(selectiDB);
  const dartMap = useSelector(selectMap);
  const dispatch = useDispatch();
  const ccmsversion = useSelector(selectVersion);

  const cleanHtml = useCallback((raw) => {
    if (!raw) return "";

    try {
      const dom = new DOMParser().parseFromString(raw, "text/html");

      const cbody =
        dom?.getElementsByTagName("conbody")[0] ||
        dom?.getElementsByTagName("body")[0];

      if (cbody) {
        cbody.querySelectorAll("xref").forEach((xref) => {
          const href = xref.getAttribute("href");
          if (href) {
            const a = document.createElement("a");
            a.href = href;
            a.target = "_blank";
            a.rel = "noopener noreferrer";
            a.innerHTML = xref.innerHTML || href;
            xref.replaceWith(a);
          }
        });

        cbody.querySelectorAll("ditatitle").forEach((title) => {
          const divElement = document.createElement("div");
          divElement.innerHTML = title.innerHTML;
          divElement.classList.add("emg-ditatitle");
          title.replaceWith(divElement);
        });
      }

      return cbody ? cbody.outerHTML : raw;
    } catch (err) {
      console.error("Error cleaning HTML:", err);
      return raw;
    }
  }, []);

  const loadContent = useCallback(
    async (contentLink) => {
      if (!contentLink) {
        console.error("Invalid content link");
        throw new Error("Invalid content link");
      }

      try {
        console.log("Attempting to load from IndexedDB:", contentLink);
        const iDBValue = await iDB.read(contentLink);
        if (iDBValue) return iDBValue;

        console.log("Not found in IndexedDB, fetching from server...");
        const action = await dispatch(
          fetchMap({
            path: contentLink,
            dartSelectors: { iDB, dispatch, ccmsversion },
          }),
        );

        const data = action.payload?.data;
        if (!data) throw new Error("No content data received");

        console.log("Successfully fetched from server");
        return data;
      } catch (err) {
        console.error("Failed to load content:", err);
        throw err;
      }
    },
    [iDB, dispatch, ccmsversion],
  );

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!dartMap || !iDB || (chapterRoot && !chapterRoot)) {
          throw new Error("Required data not loaded");
        }

        let contentLink;

        if (link) {
          // Using provided link
          contentLink = link;
        } else {
          // Generating menu from XML...
          const parsed = generateMenuFromXml(`${dartMap}`);

          contentLink = parsed?.relatedLinks
            ?.find((rl) => rl.href?.toLowerCase()?.includes(contentPattern))
            ?.href?.replace(/^(\.\.\/)+/, "");

          if (!contentLink) {
            const chapterHref = new DOMParser()
              .parseFromString(`${dartMap}`, "text/xml")
              .querySelector("chapter[href]")
              ?.getAttribute("href");

            if (chapterHref) {
              const chapterXml = await loadContent(chapterHref);
              const match = Array.from(
                new DOMParser()
                  .parseFromString(`${chapterXml}`, "text/xml")
                  .querySelectorAll("related-links > link"),
              ).find((l) =>
                l.getAttribute("href")?.toLowerCase()?.includes(contentPattern),
              );
              contentLink = match
                ?.getAttribute("href")
                ?.replace(/^(\.\.\/)+/, "");
            }
          }
        }

        if (!contentLink) {
          throw new Error(
            `Content link (${contentPattern} ) not found in document and no explicit link provided`,
          );
        }

        const raw = await loadContent(contentLink);

        if (isMounted) {
          const cleaned = cleanHtml(raw);
          setHtmlContent(cleaned || "No content available");
          setError(null);
        }
      } catch (err) {
        console.error("Content loading error:", err);
        if (isMounted) {
          setError(err.message);
          setHtmlContent("");
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [iDB, dartMap, chapterRoot, link, loadContent, cleanHtml, contentPattern]);

  return { htmlContent, isLoading, error };
};
